import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import NotificationSnackbar from "./NotificationBar";

function AddNewQualification({ open, onClose, onSaveSuccess }) {
  const theme = useTheme();
  const [form, setForm] = useState({
    qualification: "",
    degree: null,
    qualificationField: null,
  });
  const [degrees, setDegrees] = useState([]);
  const [jobFields, setJobFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  useEffect(() => {
    if (open) {
      fetchQualificationDetails();
    }
  }, [open]);

  const fetchQualificationDetails = async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/support/qualification-details");
      const data = await res.json();
      setDegrees(data.degrees || []);
      setJobFields(data.jobFields || []);
    } catch (error) {
      console.error("Failed to fetch qualification details", error);
      setSnackbarMessage("Failed to load qualification details");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, qualification: e.target.value });
  };

  const handleSubmit = async () => {
    if (!form.qualification || !form.degree?.id || !form.qualificationField?.id) return;

    try {
      const res = await fetch("/api/support/insert-qualification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          qualification: form.qualification,
          degreeId: form.degree.id,
          qualificationFieldId: form.qualificationField.id,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch {
        throw new Error("Server did not return valid JSON");
      }

      if (!res.ok) {
        throw new Error(data?.message || "Server error occurred");
      }

      setSnackbarMessage(data?.message || "Qualification added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setForm({ qualification: "", degree: null, qualificationField: null });

      setTimeout(() => {
        onSaveSuccess();
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Submit error:", error);
      setSnackbarMessage(error.message || "Something went wrong");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": { borderRadius: 3 },
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: theme.palette.primary.main,
          color: "white",
          fontWeight: 600,
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Add New Qualification
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Qualification"
                name="qualification"
                value={form.qualification}
                onChange={handleChange}
                autoFocus
                placeholder="e.g. Master's Degree in Law"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={degrees}
                value={form.degree}
                onChange={(e, newValue) =>
                  setForm({ ...form, degree: newValue })
                }
                getOptionLabel={(option) => option?.degree || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Degree"
                    placeholder="Type to search degree..."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={jobFields}
                value={form.qualificationField}
                onChange={(e, newValue) =>
                  setForm({ ...form, qualificationField: newValue })
                }
                getOptionLabel={(option) => option?.jobField || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Qualification Field"
                    placeholder="Type to search field..."
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !form.qualification || !form.degree || !form.qualificationField
          }
        >
          Save Qualification
        </Button>
      </DialogActions>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Dialog>
  );
}

export default AddNewQualification;